import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { Input } from '@/components/ui/input';
import { MagnifyingGlass, BookOpen, ChartLine, GraduationCap, Books, Brain, VideoCamera, Article, Play, GraduationCap as GradIcon } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KnowledgeHub = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [articles, setArticles] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);

  const categories = [
    { slug: 'basics-of-trading', label: t('basicsOfTrading'), icon: BookOpen, color: 'from-blue-500 to-blue-700' },
    { slug: 'fundamental-analysis', label: t('fundamentalAnalysis'), icon: ChartLine, color: 'from-purple-500 to-purple-700' },
    { slug: 'technical-analysis', label: t('technicalAnalysis'), icon: ChartLine, color: 'from-emerald-500 to-emerald-700' },
    { slug: 'traders-glossary', label: t('tradersGlossary'), icon: Books, color: 'from-amber-500 to-amber-700' },
    { slug: 'trader-psychology', label: t('traderPsychology'), icon: Brain, color: 'from-pink-500 to-pink-700' },
    { slug: 'cme-education', label: t('cmeEducation'), icon: GradIcon, color: 'from-indigo-500 to-indigo-700' },
    { slug: 'articles', label: t('articles'), icon: Article, color: 'from-slate-500 to-slate-700' },
    { slug: 'video-webinars', label: t('videoWebinars'), icon: VideoCamera, color: 'from-red-500 to-red-700' },
    { slug: 'fxpro-webinars', label: t('fxproWebinars'), icon: Play, color: 'from-orange-500 to-orange-700' },
    { slug: 'watch-and-learn', label: t('watchAndLearn'), icon: Play, color: 'from-cyan-500 to-cyan-700' },
  ];

  useEffect(() => {
    fetchArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, language]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const params = { language };
      if (selectedCategory) params.category = selectedCategory;
      const response = await axios.get(`${API}/knowledge/articles`, { params });
      setArticles(response.data);
    } catch (e) {
      console.error('Failed to fetch articles');
    } finally {
      setLoading(false);
    }
  };

  const featured = articles.filter(a => a.featured).slice(0, 3);
  const latest = articles.slice(0, 12);
  const filtered = search
    ? articles.filter(a =>
        (a.title || '').toLowerCase().includes(search.toLowerCase()) ||
        (a.excerpt || '').toLowerCase().includes(search.toLowerCase())
      )
    : articles;

  return (
    <div className="min-h-screen bg-white" data-testid="knowledge-hub-page">
      {/* Hero */}
      <section className="py-16 px-6 bg-gradient-to-br from-brand-primary to-brand-secondary text-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="text-xs uppercase tracking-widest text-blue-200 font-bold mb-3">Education Center</div>
          <h1 className="text-4xl sm:text-6xl font-bold mb-4">{t('khTitle')}</h1>
          <p className="text-xl text-slate-200 mb-8 max-w-2xl mx-auto">{t('khSubtitle')}</p>
          <div className="max-w-xl mx-auto relative">
            <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder={t('searchArticles')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-12 h-14 text-lg bg-white text-slate-900"
              data-testid="kh-search-input"
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="kh-categories-grid">
            {categories.map(cat => (
              <button
                key={cat.slug}
                onClick={() => setSelectedCategory(selectedCategory === cat.slug ? '' : cat.slug)}
                data-testid={`kh-category-${cat.slug}`}
                className={`group relative bg-white p-6 rounded-2xl border-2 hover:shadow-xl transition-all overflow-hidden text-left ${
                  selectedCategory === cat.slug ? 'border-brand-accent' : 'border-slate-200'
                }`}
              >
                <div className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${cat.color} opacity-10 rounded-full -mr-12 -mt-12 group-hover:scale-150 transition-transform`}></div>
                <div className="relative">
                  <cat.icon size={28} weight="duotone" className="text-brand-accent mb-3" />
                  <h3 className="font-bold text-slate-800">{cat.label}</h3>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      {featured.length > 0 && (
        <section className="py-16 px-6 bg-slate-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold text-brand-primary mb-8">{t('featuredArticles')}</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {featured.map(a => (
                <Link key={a.id} to={`/knowledge/${a.slug}`} className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-shadow" data-testid={`kh-featured-${a.slug}`}>
                  {a.image_url && (
                    <div className="h-48 bg-slate-100 overflow-hidden">
                      <img src={a.image_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-wider text-brand-accent font-bold mb-2">{(a.category_slug||'').replace(/-/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</div>
                    <h3 className="text-xl font-bold text-slate-800 mb-2">{a.title}</h3>
                    <p className="text-sm text-slate-600 line-clamp-2">{a.excerpt}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Latest Articles */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-brand-primary">{t('latestArticles')}</h2>
            {selectedCategory && (
              <button onClick={() => setSelectedCategory('')} className="text-sm text-brand-accent hover:underline">Clear filter</button>
            )}
          </div>
          {loading ? <div className="loader mx-auto"></div>
            : filtered.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <Article size={48} weight="duotone" className="mx-auto mb-4 text-slate-300" />
                <p>No articles available yet.</p>
                <p className="text-sm mt-2">Admins can create articles in the Admin → Knowledge Hub panel.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="kh-articles-grid">
                {filtered.map(a => (
                  <Link key={a.id} to={`/knowledge/${a.slug}`} className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl transition-shadow" data-testid={`kh-article-${a.slug}`}>
                    {a.image_url && (
                      <div className="h-40 bg-slate-100 overflow-hidden">
                        <img src={a.image_url} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="text-xs uppercase tracking-wider text-brand-accent font-bold mb-2">{(a.category_slug||'').replace(/-/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</div>
                      <h3 className="text-lg font-bold text-slate-800 mb-2 line-clamp-2">{a.title}</h3>
                      <p className="text-sm text-slate-600 line-clamp-3">{a.excerpt}</p>
                      <div className="mt-4 text-sm font-semibold text-brand-accent">{t('readMore')} →</div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
        </div>
      </section>

      {/* Demo Account CTA */}
      <section className="py-16 px-6 bg-gradient-to-br from-brand-primary to-brand-secondary text-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl font-bold mb-4">{t('openDemoAccount')}</h2>
          <p className="text-lg text-slate-200 mb-8">{t('demoBalanceCta')}</p>
          <button onClick={() => navigate('/register')} className="px-8 py-4 bg-white text-brand-primary font-bold rounded-lg hover:shadow-2xl" data-testid="kh-open-demo-btn">
            {t('createFreeAccount')}
          </button>
        </div>
      </section>
    </div>
  );
};

export default KnowledgeHub;
